import { Row, Col, Form } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import DatePicker from "react-datepicker";
import ReCAPTCHA from "react-google-recaptcha";
import moment from "moment";
import AdmissionCountries from "../AdmissionCountries";
import AdmissionSiblings from "../AdmissionSiblings";
import FilePicker from "../FilePicker";
import SectionSeparator from "../SectionSeparator";
import SubSectionSeparator from "../SubSectionSeparator";

const StudentDetailsStep = ({ ctx }) => {
  const {
    recaptcha,
    formData,
    setFormData,
    errors,
    countries,
    languages,
    ageGroup,
    handleChange,
    picture,
    setPicture,
  } = ctx;

  return (
    <>
      <Row>
        <Col md={6} lg={3} xl={4} xxl={2}>
          <FilePicker
            name="picture"
            file={picture}
            setFile={setPicture}
            height={200}
          />
        </Col>
        <Col md={6} lg={3} xxl={2} className="d-flex align-items-center">
          <FormattedMessage id="child_picture" />
        </Col>

        <Col className="ml-lg-auto d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-end gap-5 flex-column-reverse mt-5 lg:mt-3">
          <ReCAPTCHA
            ref={recaptcha}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          />
        </Col>
      </Row>

      {/* Child Details */}
      <SectionSeparator title="title.child_details" />
      <Row>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="child_name">
              {(placeholder) => (
                <Form.Control
                  value={formData.child_name}
                  placeholder={placeholder}
                  type="text"
                  name="child_name"
                  onChange={handleChange}
                  isInvalid={!!errors.child_name}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.child_name" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4">
            <FormattedMessage id="child_family_name">
              {(placeholder) => (
                <Form.Control
                  value={formData.child_family_name}
                  placeholder={placeholder}
                  type="text"
                  name="child_family_name"
                  onChange={handleChange}
                />
              )}
            </FormattedMessage>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="birth_date">
              {(placeholder) => (
                <DatePicker
                  placeholderText={placeholder}
                  className={
                    errors.birth_date
                      ? " form-control is-invalid"
                      : "form-control"
                  }
                  selected={formData.birth_date}
                  onChange={(date) => {
                    if (date) {
                      const formattedDate = moment(date).format("YYYY-MM-DD");
                      setFormData((prev) => ({
                        ...prev,
                        birth_date: formattedDate,
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        birth_date: null,
                      }));
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  isClearable
                />
              )}
            </FormattedMessage>
            <div className={errors.birth_date ? "is-invalid" : ""}></div>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.birth_date" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <Form.Select
              name="gender"
              onChange={handleChange}
              value={formData.gender}
              isInvalid={!!errors.gender}
            >
              <option value="">
                <FormattedMessage id="select_gender" />
              </option>
              <option value="F">
                <FormattedMessage id="female" />
              </option>
              <option value="M">
                <FormattedMessage id="male" />
              </option>
            </Form.Select>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.gender" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <Form.Select
              onChange={handleChange}
              name="country_id"
              value={formData.country_id}
              isInvalid={!!errors.country_id}
            >
              <option value="">
                <FormattedMessage id="select_nationality" />
              </option>
              {countries.map((country, index) => (
                <option key={index} value={country.country_id}>
                  {country.name_en}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.country_id" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="birth_city">
              {(placeholder) => (
                <Form.Control
                  value={formData.birth_city}
                  placeholder={placeholder}
                  type="text"
                  name="birth_city"
                  onChange={handleChange}
                  isInvalid={!!errors.birth_city}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.birth_city" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="id_number">
              {(placeholder) => (
                <Form.Control
                  value={formData.id_number}
                  placeholder={placeholder}
                  type="text"
                  name="id_number"
                  onChange={handleChange}
                  isInvalid={!!errors.id_number}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.id_number" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <Form.Select
              name="agegroup_id"
              onChange={handleChange}
              value={formData.agegroup_id}
              isInvalid={!!errors.agegroup_id}
            >
              <option value="">
                <FormattedMessage id="agegroup_id" />
              </option>
              {ageGroup?.map((language, index) => (
                <option key={index} value={language.agegroup_id}>
                  {language.agegroup_code}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.agegroup_id" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4">
            {/* <Form.Label>
                            <FormattedMessage id="1st_language" />
                          </Form.Label> */}
            <Form.Select
              name="first_lang"
              onChange={handleChange}
              value={formData.first_lang}
            >
              <option value="">
                <FormattedMessage id="1st_language" />
              </option>
              {languages.map((language, index) => (
                <option key={index} value={language.lang_name}>
                  {language.lang_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4">
            {/* <Form.Label>
                            <FormattedMessage id="2nd_language" />
                          </Form.Label> */}
            <Form.Select
              name="second_lang"
              onChange={handleChange}
              value={formData.second_lang}
            >
              <option value="">
                <FormattedMessage id="2nd_language" />
              </option>
              {languages.map((language, index) => (
                <option key={index} value={language.lang_name}>
                  {language.lang_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4">
            <Form.Select
              name="third_lang"
              onChange={handleChange}
              value={formData.third_lang}
            >
              <option value="">
                <FormattedMessage id="3rd_language" />
              </option>
              {languages.map((language, index) => (
                <option key={index} value={language.lang_name}>
                  {language.lang_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <Form.Select
              onChange={handleChange}
              name="religion"
              value={formData.religion}
              isInvalid={!!errors.religion}
            >
              <option value="">
                <FormattedMessage id="select_religion" />
              </option>
              <option value="1">
                <FormattedMessage id="other" />
              </option>
              <option value="2">
                <FormattedMessage id="muslim" />
              </option>
              <option value="3">
                <FormattedMessage id="christian" />
              </option>
            </Form.Select>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.religion" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Family Details */}
      <SectionSeparator title="title.family_details" />
      <SubSectionSeparator title="title.sponsor_parent_information" />
      <Row>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="full_name">
              {(placeholder) => (
                <Form.Control
                  value={formData.father_name}
                  placeholder={placeholder}
                  type="text"
                  name="father_name"
                  onChange={handleChange}
                  isInvalid={!!errors.father_name}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.full_name" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <Form.Select
              name="sponsor_role"
              onChange={handleChange}
              value={formData.sponsor_role}
              isInvalid={!!errors.sponsor_role}
              required
            >
              <option value="">
                <FormattedMessage id="select_role" />
              </option>
              <option value="Father">
                <FormattedMessage id="father" />
              </option>
              <option value="Mother">
                <FormattedMessage id="mother" />
              </option>
              <option value="Guardian">
                <FormattedMessage id="guardian" />
              </option>
              <option value="Other">
                <FormattedMessage id="other" />
              </option>
            </Form.Select>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.select_role" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="mobile_number">
              {(placeholder) => (
                <Form.Control
                  value={formData.father_mobile}
                  placeholder={placeholder}
                  type="text"
                  name="father_mobile"
                  onChange={handleChange}
                  isInvalid={!!errors.father_mobile}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.mobile" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4">
            <FormattedMessage id="work_number">
              {(placeholder) => (
                <Form.Control
                  value={formData.father_workphone}
                  placeholder={placeholder}
                  type="text"
                  name="father_workphone"
                  onChange={handleChange}
                />
              )}
            </FormattedMessage>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="email_address">
              {(placeholder) => (
                <Form.Control
                  value={formData.father_email}
                  placeholder={placeholder}
                  type="text"
                  name="father_email"
                  onChange={handleChange}
                  isInvalid={!!errors.father_email}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.email" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="place_of_work">
              {(placeholder) => (
                <Form.Control
                  value={formData.father_employer}
                  placeholder={placeholder}
                  type="text"
                  name="father_employer"
                  onChange={handleChange}
                  isInvalid={!!errors.father_employer}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.father_employer" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="occupation">
              {(placeholder) => (
                <Form.Control
                  value={formData.father_position}
                  placeholder={placeholder}
                  type="text"
                  name="father_position"
                  onChange={handleChange}
                  isInvalid={!!errors.father_position}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.father_position" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-4">
            <FormattedMessage id="language">
              {(placeholder) => (
                <Form.Control
                  value={formData.father_language}
                  placeholder={placeholder}
                  type="text"
                  name="father_language"
                  onChange={handleChange}
                />
              )}
            </FormattedMessage>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <Form.Select
              onChange={handleChange}
              name="father_country_id"
              value={formData.father_country_id}
              isInvalid={!!errors.father_country_id}
            >
              <option value="">
                <FormattedMessage id="select_nationality" />
              </option>
              {countries.map((country, index) => (
                <option key={index} value={country.country_id}>
                  {country.name_en}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.country_id" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="education">
              {(placeholder) => (
                <Form.Control
                  value={formData.father_education}
                  placeholder={placeholder}
                  type="text"
                  name="father_education"
                  onChange={handleChange}
                  isInvalid={!!errors.father_education}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.father_education" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <SubSectionSeparator title="title.spouse_information" />
      <Row>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="full_name">
              {(placeholder) => (
                <Form.Control
                  value={formData.mother_name}
                  placeholder={placeholder}
                  type="text"
                  name="mother_name"
                  onChange={handleChange}
                  isInvalid={!!errors.mother_name}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.mother_name" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <Form.Select
              name="spouse_role"
              onChange={handleChange}
              value={formData.spouse_role}
              isInvalid={!!errors.spouse_role}
              required
            >
              <option value="">
                <FormattedMessage id="select_role" />
              </option>
              <option value="Father">
                <FormattedMessage id="father" />
              </option>
              <option value="Mother">
                <FormattedMessage id="mother" />
              </option>
              <option value="Guardian">
                <FormattedMessage id="guardian" />
              </option>
              <option value="Other">
                <FormattedMessage id="other" />
              </option>
            </Form.Select>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.select_role" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="mobile_number">
              {(placeholder) => (
                <Form.Control
                  value={formData.mother_mobile}
                  placeholder={placeholder}
                  type="text"
                  name="mother_mobile"
                  onChange={handleChange}
                  isInvalid={!!errors.mother_mobile}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.mother_mobile" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="work_number">
              {(placeholder) => (
                <Form.Control
                  value={formData.mother_workphone}
                  placeholder={placeholder}
                  type="text"
                  name="mother_workphone"
                  onChange={handleChange}
                  isInvalid={!!errors.mother_workphone}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.mother_workphone" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="email_address">
              {(placeholder) => (
                <Form.Control
                  value={formData.mother_email}
                  placeholder={placeholder}
                  type="text"
                  name="mother_email"
                  onChange={handleChange}
                  isInvalid={!!errors.mother_email}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.mother_email" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4">
            <FormattedMessage id="place_of_work">
              {(placeholder) => (
                <Form.Control
                  value={formData.mother_employer}
                  placeholder={placeholder}
                  type="text"
                  name="mother_employer"
                  onChange={handleChange}
                />
              )}
            </FormattedMessage>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="occupation">
              {(placeholder) => (
                <Form.Control
                  value={formData.mother_position}
                  placeholder={placeholder}
                  type="text"
                  name="mother_position"
                  onChange={handleChange}
                  isInvalid={!!errors.mother_position}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.mother_position" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4">
            <FormattedMessage id="language">
              {(placeholder) => (
                <Form.Control
                  value={formData.mother_language}
                  placeholder={placeholder}
                  type="text"
                  name="mother_language"
                  onChange={handleChange}
                />
              )}
            </FormattedMessage>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4 position-relative">
            <Form.Select
              onChange={handleChange}
              name="mother_country_id"
              value={formData.mother_country_id}
              isInvalid={!!errors.mother_country_id}
            >
              <option value="">
                <FormattedMessage id="select_nationality" />
              </option>
              {countries.map((country, index) => (
                <option key={index} value={country.country_id}>
                  {country.name_en}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-4">
            <FormattedMessage id="education">
              {(placeholder) => (
                <Form.Control
                  value={formData.mother_education}
                  placeholder={placeholder}
                  type="text"
                  name="mother_education"
                  onChange={handleChange}
                />
              )}
            </FormattedMessage>
          </Form.Group>
        </Col>
      </Row>

      <SubSectionSeparator title="title.sibling_information" />
      <AdmissionSiblings
        errors={errors}
        admissionSiblings={formData.admission_siblings}
        setFormData={setFormData}
      />

      {/* <Button variant="primary" onClick={addSibling}>
                      + Add Siblings
                    </Button> */}

      <SectionSeparator title="title.emergency_contact" />
      <Row>
        <Col md={4}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="name">
              {(placeholder) => (
                <Form.Control
                  value={formData.emergency_contact1}
                  placeholder={placeholder}
                  type="text"
                  name="emergency_contact1"
                  onChange={handleChange}
                  isInvalid={!!errors.emergency_contact1}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.emergency_contact_name" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="relation">
              {(placeholder) => (
                <Form.Control
                  value={formData.emergency_relation1}
                  placeholder={placeholder}
                  type="text"
                  name="emergency_relation1"
                  onChange={handleChange}
                  isInvalid={!!errors.emergency_relation1}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.emergency_contact_relation" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="contact_number">
              {(placeholder) => (
                <Form.Control
                  value={formData.emergency_phone1}
                  placeholder={placeholder}
                  type="text"
                  name="emergency_phone1"
                  onChange={handleChange}
                  isInvalid={!!errors.emergency_phone1}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.emergency_contact_number" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>

            <FormattedMessage id="name">
              {(placeholder) => (
                <Form.Control
                  value={formData.emergency_contact2}
                  placeholder={placeholder}
                  type="text"
                  name="emergency_contact2"
                  onChange={handleChange}
                  isInvalid={!!errors.emergency_contact2}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.emergency_contact_name" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>
            <FormattedMessage id="relation">
              {(placeholder) => (
                <Form.Control
                  value={formData.emergency_relation2}
                  placeholder={placeholder}
                  type="text"
                  name="emergency_relation2"
                  onChange={handleChange}
                  isInvalid={!!errors.emergency_relation2}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.emergency_contact_relation" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-4 position-relative">
            <span className="required-field">*</span>

            <FormattedMessage id="contact_number">
              {(placeholder) => (
                <Form.Control
                  value={formData.emergency_phone2}
                  placeholder={placeholder}
                  type="text"
                  name="emergency_phone2"
                  onChange={handleChange}
                  isInvalid={!!errors.emergency_phone2}
                />
              )}
            </FormattedMessage>
            <Form.Control.Feedback className="px-3" type="invalid">
              <FormattedMessage id="error.emergency_contact_number" />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <SectionSeparator title="title.other_contact" />
      <Row>
        <Col md={6}>
          <SubSectionSeparator title="nanny" />
          <Row>
            <Col md={6}>
              <Form.Group className="mb-4 position-relative">
                <span className="required-field">*</span>
                <FormattedMessage id="nanny_driver">
                  {(placeholder) => (
                    <Form.Control
                      value={formData.nanny_name}
                      placeholder={placeholder}
                      type="text"
                      name="nanny_name"
                      onChange={handleChange}
                      isInvalid={!!errors.nanny_name}
                    />
                  )}
                </FormattedMessage>
                <Form.Control.Feedback className="px-3" type="invalid">
                  <FormattedMessage id="error.nanny_name" />
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-4 position-relative">
                <span className="required-field">*</span>
                <FormattedMessage id="nanny_driver_phone">
                  {(placeholder) => (
                    <Form.Control
                      value={formData.nanny_phone}
                      placeholder={placeholder}
                      type="text"
                      name="nanny_phone"
                      onChange={handleChange}
                      isInvalid={!!errors.nanny_phone}
                    />
                  )}
                </FormattedMessage>
                <Form.Control.Feedback className="px-3" type="invalid">
                  <FormattedMessage id="error.nanny_phone" />
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Col>
        <Col md={6}>
          <SubSectionSeparator title="driver" />
          <Row>
            <Col md={6}>
              <Form.Group className="mb-4 position-relative">
                <span className="required-field">*</span>
                <FormattedMessage id="nanny_driver">
                  {(placeholder) => (
                    <Form.Control
                      value={formData.driver_name}
                      placeholder={placeholder}
                      type="text"
                      name="driver_name"
                      onChange={handleChange}
                      isInvalid={!!errors.driver_name}
                    />
                  )}
                </FormattedMessage>
                <Form.Control.Feedback className="px-3" type="invalid">
                  <FormattedMessage id="error.driver_name" />
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-4 position-relative">
                <span className="required-field">*</span>
                <FormattedMessage id="nanny_driver_phone">
                  {(placeholder) => (
                    <Form.Control
                      value={formData.driver_phone}
                      placeholder={placeholder}
                      type="text"
                      name="driver_phone"
                      onChange={handleChange}
                      isInvalid={!!errors.driver_phone}
                    />
                  )}
                </FormattedMessage>
                <Form.Control.Feedback className="px-3" type="invalid">
                  <FormattedMessage id="error.driver_phone" />
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>

      <SectionSeparator title="title.admission_countries" />

      <AdmissionCountries
        admissioncountries={formData.admissioncountries}
        errors={errors}
        setFormData={setFormData}
      />
      {/* Note Section 
                    <div className="mt-5 p-3 border rounded bg-light">
                      <h6 className="fw-bold">
                        <FormattedMessage id="note.title" />
                      </h6>
                      <ul className="mb-0">
                        <li>
                          <FormattedMessage id="note.point1" />
                        </li>
                        <li>
                          <FormattedMessage id="note.point2" />
                        </li>
                      </ul>
                    </div>*/}
    </>
  );
};

export default StudentDetailsStep;
